import React, { useEffect } from 'react';

interface UseScrollAnchorProps {
  isContentReady: boolean;
  scrollTo?: string;
  reactionsRef: React.RefObject<HTMLDivElement>;
  commentsRef: React.RefObject<HTMLDivElement>;
  scrollContainerId: string;
}

export const useScrollAnchor = ({
  isContentReady,
  scrollTo,
  reactionsRef,
  commentsRef,
  scrollContainerId,
}: UseScrollAnchorProps) => {
  useEffect(() => {
    if (!isContentReady || !scrollTo) return;

    const targetRef = scrollTo === 'reactions' ? reactionsRef : commentsRef;
    const scrollContainer = document.getElementById(scrollContainerId);

    if (!targetRef.current) return;

    let userInteracted = false;
    let hasInitiallyScrolled = false;
    const handleUserInteraction = () => {
      userInteracted = true;
    };

    if (scrollContainer) {
      scrollContainer.addEventListener('wheel', handleUserInteraction, {
        passive: true,
      });
      scrollContainer.addEventListener('touchstart', handleUserInteraction, {
        passive: true,
      });
      scrollContainer.addEventListener('mousedown', handleUserInteraction, {
        passive: true,
      });
    }

    const doScroll = (behavior: ScrollBehavior = 'auto') => {
      if (userInteracted || !targetRef.current) return;
      targetRef.current.scrollIntoView({
        behavior,
        block: scrollTo === 'reactions' ? 'center' : 'start',
      });
    };

    // Initial smooth scroll: wait 400ms for the 300ms fadeSlideIn animation to finish
    const timeoutId = setTimeout(() => {
      doScroll('smooth');
      // Enable ResizeObserver snapping only after initial scroll has started
      setTimeout(() => {
        hasInitiallyScrolled = true;
      }, 100);
    }, 400);

    // Watch for layout shifts (like late-loading images) and snap back instantly
    let resizeCount = 0;
    const observer = new ResizeObserver(() => {
      if (userInteracted) return;
      resizeCount++;
      // Only fix layout shifts AFTER the initial smooth scroll has taken place
      if (resizeCount > 1 && hasInitiallyScrolled) {
        doScroll('auto');
      }
    });

    if (scrollContainer && scrollContainer.firstElementChild) {
      observer.observe(scrollContainer.firstElementChild);
    }

    const cleanupTimeout = setTimeout(() => {
      observer.disconnect();
    }, 5000); // Give up on forcing scroll after 5 seconds

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(cleanupTimeout);
      observer.disconnect();
      if (scrollContainer) {
        scrollContainer.removeEventListener('wheel', handleUserInteraction);
        scrollContainer.removeEventListener(
          'touchstart',
          handleUserInteraction
        );
        scrollContainer.removeEventListener('mousedown', handleUserInteraction);
      }
    };
  }, [isContentReady, scrollTo, reactionsRef, commentsRef, scrollContainerId]);
};
